/**
 * BFF read/write client — Surface Layer (/01)
 *
 * The Surface's synchronous GET/PATCH path to the same-origin public BFF
 * (`/api/*`). Unlike ActionFrames (which the BFF only *enqueues*), the
 * backend-owned read endpoints return data directly:
 *
 *   GET   /api/user-settings?scope_type=&scope_key=   → granular settings
 *   GET   /api/surface-contracts?kind=                → view/plugin/etc contracts
 *   PATCH /api/user-settings                          → persist user prefs
 *
 * Every call attaches the session JWT as `Authorization: Bearer`
 * (read from the vendor-neutral session-token holder). When no session
 * exists the call resolves to a typed `unauthorized` BridgeResponse so
 * callers fall back to their mock baseline rather than throwing.
 *
 * No SQL, no shell, no direct DB/payment SDK — only the registered BFF
 * HTTP surface. Business data never bypasses this seam.
 */

import type { BridgeError, BridgeResponse } from '@contracts/idl-bridge.contract';
import { getAccessToken } from '@auth/session-token';

const DEFAULT_TIMEOUT_MS = 8000;

/** Same-origin public BFF paths (proxied to the live Worker in dev). */
export const SURFACE_BFF_PATHS = {
  userSettings: '/api/user-settings',
  surfaceContracts: '/api/surface-contracts',
  documents: '/api/documents',
  reports: '/api/reports',
  onboardingDraft: '/api/onboarding-draft',
  // B2-B read-only seams (flag-gated; endpoints go live in B2-B-2):
  //   tenant-memberships ← RemoteUsersDatabase RLS (TenantPlane);  tool-routes ← localserver/ToolGateway (DevHub).
  tenantMemberships: '/api/tenant-memberships',
  toolRoutes: '/api/tool-routes',
  // REQ-001.72 Client Workroom staging-backed read seams (flag VITE_CLIENT_WORKROOM_LIVE_READS).
  // All read-only, tenant-resolved server-side, RLS-isolated. Browser never holds a DB client.
  clientWorkroomContext: '/api/client-workroom/context',
  clientWorkroomBrandIdentity: '/api/client-workroom/brand-identity-profile',
  clientWorkroomDocuments: '/api/client-workroom/documents',
  clientWorkroomPosts: '/api/client-workroom/posts',
  clientWorkroomCalendar: '/api/client-workroom/calendar',
  clientWorkroomNotifications: '/api/client-workroom/notifications',
  clientWorkroomWallet: '/api/client-workroom/wallet',
  // REQ-001.72 DevHub read seams.
  devhubRuntimeSummary: '/api/devhub/runtime-summary',
  devhubSpokes: '/api/devhub/spokes',
  devhubPlugins: '/api/devhub/plugins',
  devhubActionframes: '/api/devhub/actionframes',
} as const;

export interface BffRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

type QueryValue = string | number | undefined;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length > 0 ? `${path}?${parts.join('&')}` : path;
}

function bridgeErrorFor(status: number, detail?: string): BridgeError {
  const code: BridgeError['code'] =
    status === 401 ? 'unauthorized'
    : status === 403 ? 'forbidden'
    : status === 404 ? 'not_found'
    : status === 413 ? 'payload_too_large'
    : status === 422 ? 'validation_failed'
    : status === 429 ? 'rate_limited'
    : 'unknown';
  return {
    code,
    message: detail ? `BFF ${status}: ${detail}` : `BFF responded with HTTP ${status}`,
    retryable: status >= 500 || status === 429,
  };
}

async function bffRequest<TData>(
  method: 'GET' | 'PATCH' | 'POST',
  path: string,
  query?: Record<string, QueryValue>,
  body?: unknown,
  options: BffRequestOptions = {}
): Promise<BridgeResponse<TData>> {
  const token = getAccessToken();
  if (!token) {
    return {
      ok: false,
      error: {
        code: 'unauthorized',
        message: 'No active session token; sign in to load backend data.',
        retryable: false,
      },
    };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  try {
    const response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal ?? controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      let detail: string | undefined;
      try {
        const errBody = (await response.json()) as { status?: string; detail?: string };
        detail = errBody.detail ?? errBody.status;
      } catch {
        detail = undefined;
      }
      return { ok: false, error: bridgeErrorFor(response.status, detail) };
    }

    const data = (await response.json()) as TData;
    return { ok: true, data };
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err instanceof DOMException && err.name === 'AbortError';
    return {
      ok: false,
      error: {
        code: 'bridge_unavailable',
        message: isAbort
          ? `BFF call timed out after ${options.timeoutMs ?? DEFAULT_TIMEOUT_MS}ms`
          : `BFF unreachable: ${(err as Error).message ?? 'unknown'}`,
        retryable: true,
      },
    };
  }
}

export function bffGet<TData>(
  path: string,
  query?: Record<string, QueryValue>,
  options?: BffRequestOptions
): Promise<BridgeResponse<TData>> {
  return bffRequest<TData>('GET', path, query, undefined, options);
}

export function bffPatch<TData>(
  path: string,
  body: unknown,
  query?: Record<string, QueryValue>,
  options?: BffRequestOptions
): Promise<BridgeResponse<TData>> {
  return bffRequest<TData>('PATCH', path, query, body, options);
}

export function bffPost<TData>(
  path: string,
  body?: unknown,
  query?: Record<string, QueryValue>,
  options?: BffRequestOptions
): Promise<BridgeResponse<TData>> {
  return bffRequest<TData>('POST', path, query, body, options);
}
